import { useState, useCallback, useEffect, useMemo } from "react";
import {
  X,
  Plus,
  Pencil,
  LayoutList,
  FileText,
  Loader2,
  ScanText,
  Sigma,
  TestTube2,
} from "lucide-react";
import { cn } from "../../../../../lib/utils";
import { ConfirmDeleteDialog } from "../../../../../components/audit/collab/ConfirmDeleteDialog";

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../../../components/ui/card";
import { Badge } from "../../../../../components/ui/badge";
import { Button } from "../../../../../components/ui/button";
import { Input } from "../../../../../components/ui/input";
import { Textarea } from "../../../../../components/ui/textarea";
import { Label } from "../../../../../components/ui/label";
import {
  ScrollArea,
  ScrollBar,
} from "../../../../../components/ui/scroll-area";
import { AutoResizingTextarea } from "../../../../../components/AutoResizingTextarea";
import { OptroManagedField } from "../../../../../components/audit/OptroManagedField";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../../../components/ui/dialog";
import {
  WorkStepDraftPlan,
  WorkStepPlanPatch,
  WorkStepProposedAttribute,
  WorkStepDraftSupportColumn,
  WorkStepDraftAttributeTestColumn,
  WorkStepDraftTestingTable,
  WorkStepProcedureType,
} from "../../../../../models/audit/collabWorkStepSession";
import { useHybridTestingAvailability } from "../../../../../hooks/useHybridTestingAvailability";
import {
  directChoiceHold,
  procedureTypeHold,
  sampledColumnAddHold,
  type SeededBases,
} from "../../../../../components/audit/collab/directTableRule";
import { ProcedureTypeField } from "../../../../../components/audit/collab/ProcedureTypeField";
import {
  FieldDiff,
  PlanTab,
  RemovedPlanItems,
  computeRemovedPlanItems,
  fieldKeys,
  tabForFieldKey,
  withRemovedInPlace,
} from "./planFreeTextDiff";
import {
  EDIT_CARD_CLASS,
  PlanFieldDiffContext,
  PlanHighlightContext,
  PlanRevealContext,
  PlanRevealTarget,
  DeleteRequest,
  RequestDelete,
  previousValueFor,
  useCommittableDraft,
} from "../../../../../components/audit/collab/planReveal";
import {
  CollapsibleSection,
  EditableAttributeReference,
  EditableDetailField,
  Field,
  FieldRevealAnchor,
  RemovedBadge,
  WithPreviousValue,
} from "../../../../../components/audit/collab/planFields";
import { CollabTestInstructionsView } from "../../../../../components/audit/collab/CollabTestInstructionsView";
import { UntestedAttributesNotice } from "../../../../../components/audit/collab/UntestedAttributesNotice";
export type { PlanRevealTarget } from "../../../../../components/audit/collab/planReveal";

/**
 * Fork of controls/components/collab/CollabPlanDisplay.tsx for the work-step
 * collab wizard, renamed WorkStepPlanDisplay. It has no control-type selector
 * and no key-reports / workpaper-examples sections — WorkStepDraftPlan only has
 * name/description/attributes/testingTables/reasoning. A testing table is
 * "sample" or "direct". The Procedure Type picker appears only when editing a
 * table where hybrid testing is not hidden for work steps, because only an
 * existing table can carry the Analytics columns a direct table needs; an
 * added table is sample, and an edit without the picker keeps the table's own
 * procedure type.
 */

interface WorkStepPlanDisplayProps {
  plan: WorkStepDraftPlan;
  editable?: boolean;
  onPlanChange?: (patch: WorkStepPlanPatch) => void;
  disabled?: boolean;
  /** Small label shown above the work step name in the card header. */
  headerLabel?: string;
  /** True while a direct edit (field change, restore) is being acked by the
   * server — renders an ambient "Saving…" indicator in the header. */
  saving?: boolean;
  /** Free-text diffs from the most recent revision turn. Changed fields render
   *  the old value struck through above the new value. */
  fieldDiffs?: FieldDiff[];
  /** The plan as it stood before the most recent revision turn; items removed
   *  by that turn render as red in-place ghosts. */
  previousPlan?: WorkStepDraftPlan | null;
  /** Field the user asked to see (e.g. via a chat link); see PlanRevealTarget. */
  revealTarget?: PlanRevealTarget | null;
  /** Field to keep highlighted. Persists (amber ring) until another field is revealed
   *  or a new edit/chat clears it; independent of the one-shot reveal scroll. */
  highlightedFieldKey?: string | null;
  /** Plan fields managed in Optro for this session (e.g. "work_step_name"):
   *  shown read-only with the same explanation as the configuration page. */
  optroLockedFields?: readonly string[];
}

const NO_REMOVED_ITEMS: RemovedPlanItems = {
  attributes: [],
  tables: [],
  testColumns: new Map(),
  supportColumns: new Map(),
  analyticsColumns: new Map(),
};

const PLAN_TABS: { id: PlanTab; label: string; icon: typeof LayoutList }[] = [
  { id: "structure", label: "Test Structure", icon: LayoutList },
  { id: "instructions", label: "Test Instructions", icon: FileText },
];

/**
 * Read-only, horizontally-scrolling spreadsheet-style view of a testing table's
 * columns — one `<th>` per support/attribute-test column, mirroring the control
 * view's WorkPaperTemplate table. The body row shows each column's type.
 */
const ColumnsTable = ({ table }: { table: WorkStepDraftTestingTable }) => {
  const columns: {
    key: string;
    name: string;
    icon: typeof ScanText;
    typeLabel: string;
  }[] = [
    ...table.supportColumns.map((col, i) => ({
      key: `support-${i}`,
      name: col.name,
      icon: ScanText,
      typeLabel: col.dataType === "string" ? "Text" : "Number",
    })),
    ...table.attributeTestColumns.map((col, i) => ({
      key: `attr-${i}`,
      name: col.name ?? col.attributeReference ?? col.passCriteria,
      icon: TestTube2,
      typeLabel: col.attributeReference
        ? `Attribute Test · ${col.attributeReference}`
        : "Attribute Test",
    })),
    ...table.analyticsColumns.map((col, i) => ({
      key: `analytics-${i}`,
      name: col.name,
      icon: Sigma,
      // Only a proposal is known to need setup; the plan does not carry a
      // saved column's workflow state.
      typeLabel: [
        "Analytics",
        col.attributeReference,
        col.id ? null : "Needs setup",
      ]
        .filter(Boolean)
        .join(" · "),
    })),
    ...table.removedAnalyticsColumns.map((col, i) => ({
      key: `removed-analytics-${i}`,
      name: col.name,
      icon: Sigma,
      typeLabel: "Analytics · Removed on save",
    })),
  ];

  if (columns.length === 0) return null;

  return (
    <ScrollArea
      type="always"
      className="rounded-md border border-stone-200 bg-white shadow-sm"
    >
      <table className="mb-2.5 min-w-full table-fixed">
        <thead className="border-b border-stone-200 bg-stone-50">
          <tr>
            {columns.map((col) => {
              const Icon = col.icon;
              return (
                <th
                  key={col.key}
                  className="w-[200px] min-w-[200px] border-r border-stone-200 px-3 py-2 text-left text-xs font-medium text-stone-700 last:border-r-0"
                >
                  <span className="flex items-center gap-1.5">
                    <Icon className="h-3.5 w-3.5 shrink-0 text-stone-400" />
                    <span className="truncate" title={col.name}>
                      {col.name}
                    </span>
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          <tr>
            {columns.map((col) => (
              <td
                key={col.key}
                className="w-[200px] min-w-[200px] border-r border-stone-200 px-3 py-2 text-xs text-stone-500 last:border-r-0"
              >
                {col.typeLabel}
              </td>
            ))}
          </tr>
        </tbody>
      </table>
      <ScrollBar
        orientation="horizontal"
        className="h-2.5 border-t-stone-200 bg-stone-50 [&>div]:bg-stone-300 [&>div]:hover:bg-stone-400"
      />
    </ScrollArea>
  );
};

interface AddAttributeDialogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (attribute: WorkStepProposedAttribute) => void;
}

const AddAttributeDialog = ({
  open,
  onClose,
  onAdd,
}: AddAttributeDialogProps) => {
  const [reference, setReference] = useState("");
  const [statement, setStatement] = useState("");

  const handleSubmit = useCallback(() => {
    if (reference.trim() && statement.trim()) {
      onAdd({ reference: reference.trim(), statement: statement.trim() });
      setReference("");
      setStatement("");
      onClose();
    }
  }, [reference, statement, onAdd, onClose]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Attribute</DialogTitle>
          <DialogDescription>
            Add a new attribute to the work step plan.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="attr-reference">Reference</Label>
            <Input
              id="attr-reference"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="e.g., A1, A2"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="attr-statement">Statement</Label>
            <Textarea
              id="attr-statement"
              value={statement}
              onChange={(e) => setStatement(e.target.value)}
              placeholder="Describe the attribute..."
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!reference.trim() || !statement.trim()}
          >
            Add
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface TestingTableDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (table: WorkStepDraftTestingTable) => void;
  attributeReferences: string[];
  initialTable?: WorkStepDraftTestingTable;
  mode: "add" | "edit";
  seededBases: SeededBases;
}

const TestingTableDialog = ({
  open,
  onClose,
  onSave,
  attributeReferences,
  initialTable,
  mode,
  seededBases,
}: TestingTableDialogProps) => {
  const [name, setName] = useState("");
  const [purpose, setPurpose] = useState("");
  const [procedureType, setProcedureType] =
    useState<WorkStepProcedureType>("sample");
  const hybridTestingAvailability = useHybridTestingAvailability("workStep");
  const [selectedAttrRefs, setSelectedAttrRefs] = useState<string[]>([]);
  const [supportColumns, setSupportColumns] = useState<
    WorkStepDraftSupportColumn[]
  >([]);
  const [attributeTestColumns, setAttributeTestColumns] = useState<
    WorkStepDraftAttributeTestColumn[]
  >([]);
  const [testingInstructions, setTestingInstructions] = useState("");
  const [documentationStandards, setDocumentationStandards] = useState("");

  // New support column form
  const [newSupportName, setNewSupportName] = useState("");
  const [newSupportDescription, setNewSupportDescription] = useState("");
  const [newSupportDataType, setNewSupportDataType] = useState<
    "string" | "number"
  >("string");

  // New attribute test column form
  const [newAttrName, setNewAttrName] = useState("");
  const [newAttrPassCriteria, setNewAttrPassCriteria] = useState("");
  const [newAttrFailCriteria, setNewAttrFailCriteria] = useState("");
  const [newAttrNaCriteria, setNewAttrNaCriteria] = useState("");
  const [newAttrProcedureGuidance, setNewAttrProcedureGuidance] = useState("");
  const [newAttrAttributeRef, setNewAttrAttributeRef] = useState<string | null>(
    null,
  );

  // Reset form when dialog opens or initialTable changes
  useEffect(() => {
    if (open) {
      if (mode === "edit" && initialTable) {
        setName(initialTable.name);
        setPurpose(initialTable.purpose);
        setProcedureType(initialTable.procedureType);
        setSelectedAttrRefs(initialTable.attributeReferences);
        setSupportColumns(initialTable.supportColumns);
        setAttributeTestColumns(initialTable.attributeTestColumns);
        setTestingInstructions(initialTable.testingContext ?? "");
        setDocumentationStandards(initialTable.documentationStandards ?? "");
      } else {
        setName("");
        setPurpose("");
        setProcedureType("sample");
        setSelectedAttrRefs([]);
        setSupportColumns([]);
        setAttributeTestColumns([]);
        setTestingInstructions("");
        setDocumentationStandards("");
      }
      setNewSupportName("");
      setNewSupportDescription("");
      setNewSupportDataType("string");
      setNewAttrName("");
      setNewAttrPassCriteria("");
      setNewAttrFailCriteria("");
      setNewAttrNaCriteria("");
      setNewAttrProcedureGuidance("");
      setNewAttrAttributeRef(null);
    }
  }, [open, mode, initialTable]);

  // Reset form function for submit handler
  const resetForm = useCallback(() => {
    setName("");
    setPurpose("");
    setProcedureType("sample");
    setSelectedAttrRefs([]);
    setSupportColumns([]);
    setAttributeTestColumns([]);
    setTestingInstructions("");
    setDocumentationStandards("");
    setNewSupportName("");
    setNewSupportDescription("");
    setNewSupportDataType("string");
    setNewAttrName("");
    setNewAttrPassCriteria("");
    setNewAttrFailCriteria("");
    setNewAttrNaCriteria("");
    setNewAttrProcedureGuidance("");
    setNewAttrAttributeRef(null);
  }, []);

  const handleAddSupportColumn = useCallback(() => {
    if (newSupportName.trim() && newSupportDescription.trim()) {
      setSupportColumns((prev) => [
        ...prev,
        {
          name: newSupportName.trim(),
          description: newSupportDescription.trim(),
          dataType: newSupportDataType,
        },
      ]);
      setNewSupportName("");
      setNewSupportDescription("");
      setNewSupportDataType("string");
    }
  }, [newSupportName, newSupportDescription, newSupportDataType]);

  const handleRemoveSupportColumn = useCallback((index: number) => {
    setSupportColumns((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleAddAttributeTestColumn = useCallback(() => {
    if (newAttrPassCriteria.trim()) {
      setAttributeTestColumns((prev) => [
        ...prev,
        {
          name: newAttrName.trim() || null,
          passCriteria: newAttrPassCriteria.trim(),
          failCriteria: newAttrFailCriteria.trim() || null,
          naCriteria: newAttrNaCriteria.trim() || null,
          procedureGuidance: newAttrProcedureGuidance.trim() || null,
          attributeReference: newAttrAttributeRef,
        },
      ]);
      setNewAttrName("");
      setNewAttrPassCriteria("");
      setNewAttrFailCriteria("");
      setNewAttrNaCriteria("");
      setNewAttrProcedureGuidance("");
      setNewAttrAttributeRef(null);
    }
  }, [
    newAttrName,
    newAttrPassCriteria,
    newAttrFailCriteria,
    newAttrNaCriteria,
    newAttrProcedureGuidance,
    newAttrAttributeRef,
  ]);

  const handleRemoveAttributeTestColumn = useCallback((index: number) => {
    setAttributeTestColumns((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleToggleAttrRef = useCallback((ref: string) => {
    setSelectedAttrRefs((prev) =>
      prev.includes(ref) ? prev.filter((r) => r !== ref) : [...prev, ref],
    );
  }, []);

  const handleSubmit = useCallback(() => {
    if (name.trim() && purpose.trim()) {
      onSave({
        name: name.trim(),
        purpose: purpose.trim(),
        procedureType,
        attributeReferences: selectedAttrRefs,
        supportColumns: supportColumns,
        attributeTestColumns: attributeTestColumns,
        // This dialog does not edit analytics columns; an edit carries the
        // table's own through so the replaced table keeps them.
        analyticsColumns:
          mode === "edit" && initialTable ? initialTable.analyticsColumns : [],
        removedAnalyticsColumns:
          mode === "edit" && initialTable
            ? initialTable.removedAnalyticsColumns
            : [],
        testingContext: testingInstructions.trim() || null,
        documentationStandards: documentationStandards.trim() || null,
      });
      resetForm();
      onClose();
    }
  }, [
    name,
    purpose,
    procedureType,
    selectedAttrRefs,
    supportColumns,
    attributeTestColumns,
    mode,
    initialTable,
    testingInstructions,
    documentationStandards,
    onSave,
    resetForm,
    onClose,
  ]);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        onClose();
      }
    },
    [onClose],
  );

  const showProcedureType =
    mode === "edit" && hybridTestingAvailability !== "hidden";
  const typeHold = procedureTypeHold(initialTable, seededBases);
  const directHold = directChoiceHold(
    initialTable,
    supportColumns,
    attributeTestColumns,
    seededBases,
  );
  const sampledColumnHold =
    hybridTestingAvailability === "hidden"
      ? null
      : sampledColumnAddHold(initialTable, procedureType, seededBases);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === "add" ? "Add Testing Table" : "Edit Testing Table"}
          </DialogTitle>
          <DialogDescription>
            {mode === "add"
              ? "Add a new testing table (work paper) to the work step plan."
              : "Edit the testing table details."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="table-name">Table Name</Label>
            <Input
              id="table-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Main Testing, Sample Completeness & Accuracy"
              disabled={mode === "edit"}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="table-purpose">Purpose</Label>
            <AutoResizingTextarea
              id="table-purpose"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
              placeholder="Brief description of what this table evaluates..."
              minHeight={60}
            />
          </div>
          {showProcedureType && (
            <ProcedureTypeField
              procedureType={procedureType}
              onProcedureTypeChange={setProcedureType}
              typeHold={typeHold}
              directHold={directHold}
            />
          )}

          {attributeReferences.length > 0 && (
            <div className="space-y-2">
              <Label>Tested Attributes</Label>
              <div className="flex flex-wrap gap-2">
                {attributeReferences.map((ref) => (
                  <Button
                    key={ref}
                    type="button"
                    variant={
                      selectedAttrRefs.includes(ref) ? "default" : "outline"
                    }
                    size="sm"
                    onClick={() => handleToggleAttrRef(ref)}
                  >
                    {ref}
                  </Button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="table-testing-context">Testing Context</Label>
            <AutoResizingTextarea
              id="table-testing-context"
              value={testingInstructions}
              onChange={(e) => setTestingInstructions(e.target.value)}
              placeholder="Optional instructions for how testing should be performed..."
              minHeight={60}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="table-documentation-standards">
              Documentation Standards
            </Label>
            <AutoResizingTextarea
              id="table-documentation-standards"
              value={documentationStandards}
              onChange={(e) => setDocumentationStandards(e.target.value)}
              placeholder="Optional documentation standards to follow..."
              minHeight={60}
            />
          </div>

          {/* Support Columns section */}
          <div className="space-y-2 border-t pt-4">
            <Label>Support Columns</Label>
            {supportColumns.length > 0 && (
              <div className="space-y-2 mb-4">
                {supportColumns.map((col, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 bg-stone-50 rounded text-sm"
                  >
                    <div>
                      <span className="font-medium">{col.name}</span>
                      <Badge variant="outline" className="text-xs ml-2">
                        {col.dataType === "string" ? "Text" : "Number"}
                      </Badge>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveSupportColumn(index)}
                      className="p-1 hover:bg-stone-200 rounded"
                    >
                      <X className="h-3 w-3 text-stone-500" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="space-y-3 p-3 border border-dashed border-stone-300 rounded">
              <div className="text-xs text-stone-500 font-medium">
                Add Support Column
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input
                  value={newSupportName}
                  onChange={(e) => setNewSupportName(e.target.value)}
                  placeholder="Column name"
                  className="text-sm"
                />
                <Select
                  value={newSupportDataType}
                  onValueChange={(v) => {
                    if (v === "string" || v === "number") {
                      setNewSupportDataType(v);
                    }
                  }}
                >
                  <SelectTrigger className="text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="string">Text</SelectItem>
                    <SelectItem value="number">Number</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <AutoResizingTextarea
                value={newSupportDescription}
                onChange={(e) => setNewSupportDescription(e.target.value)}
                placeholder="Description..."
                className="text-sm"
                minHeight={60}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddSupportColumn}
                disabled={
                  sampledColumnHold !== null ||
                  !newSupportName.trim() ||
                  !newSupportDescription.trim()
                }
                aria-describedby={
                  sampledColumnHold ? "table-support-column-hold" : undefined
                }
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Support Column
              </Button>
              {sampledColumnHold && (
                <p
                  id="table-support-column-hold"
                  className="text-xs text-stone-500"
                >
                  {sampledColumnHold}
                </p>
              )}
            </div>
          </div>

          {/* Attribute Test Columns section */}
          <div className="space-y-2 border-t pt-4">
            <Label>Attribute Test Columns</Label>
            {attributeTestColumns.length > 0 && (
              <div className="space-y-2 mb-4">
                {attributeTestColumns.map((col, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-2 bg-stone-50 rounded text-sm"
                  >
                    <div>
                      <span className="font-medium">
                        {col.name ?? col.passCriteria}
                      </span>
                      {col.attributeReference && (
                        <Badge variant="secondary" className="text-xs ml-2">
                          {col.attributeReference}
                        </Badge>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveAttributeTestColumn(index)}
                      className="p-1 hover:bg-stone-200 rounded"
                    >
                      <X className="h-3 w-3 text-stone-500" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="space-y-3 p-3 border border-dashed border-stone-300 rounded">
              <div className="text-xs text-stone-500 font-medium">
                Add Attribute Test Column
              </div>
              <Input
                value={newAttrName}
                onChange={(e) => setNewAttrName(e.target.value)}
                placeholder="Column name (optional)"
                className="text-sm"
              />
              <AutoResizingTextarea
                value={newAttrPassCriteria}
                onChange={(e) => setNewAttrPassCriteria(e.target.value)}
                placeholder="Pass criteria (required)..."
                className="text-sm"
                minHeight={60}
              />
              <AutoResizingTextarea
                value={newAttrFailCriteria}
                onChange={(e) => setNewAttrFailCriteria(e.target.value)}
                placeholder="Fail criteria..."
                className="text-sm"
                minHeight={60}
              />
              <AutoResizingTextarea
                value={newAttrNaCriteria}
                onChange={(e) => setNewAttrNaCriteria(e.target.value)}
                placeholder="N/A criteria..."
                className="text-sm"
                minHeight={60}
              />
              <AutoResizingTextarea
                value={newAttrProcedureGuidance}
                onChange={(e) => setNewAttrProcedureGuidance(e.target.value)}
                placeholder="Procedure guidance..."
                className="text-sm"
                minHeight={60}
              />
              {attributeReferences.length > 0 && (
                <Select
                  value={newAttrAttributeRef ?? ""}
                  onValueChange={(v) => setNewAttrAttributeRef(v || null)}
                >
                  <SelectTrigger className="text-sm">
                    <SelectValue placeholder="Link to attribute..." />
                  </SelectTrigger>
                  <SelectContent>
                    {attributeReferences.map((ref) => (
                      <SelectItem key={ref} value={ref}>
                        {ref}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddAttributeTestColumn}
                disabled={
                  sampledColumnHold !== null || !newAttrPassCriteria.trim()
                }
                aria-describedby={
                  sampledColumnHold
                    ? "table-attribute-test-column-hold"
                    : undefined
                }
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Attribute Test Column
              </Button>
              {sampledColumnHold && (
                <p
                  id="table-attribute-test-column-hold"
                  className="text-xs text-stone-500"
                >
                  {sampledColumnHold}
                </p>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!name.trim() || !purpose.trim()}
          >
            {mode === "add" ? "Add" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const WorkStepPlanDisplay = ({
  plan,
  editable = false,
  onPlanChange,
  disabled = false,
  headerLabel,
  saving = false,
  fieldDiffs,
  previousPlan = null,
  revealTarget = null,
  highlightedFieldKey = null,
  optroLockedFields = [],
}: WorkStepPlanDisplayProps) => {
  const [activeTab, setActiveTab] = useState<PlanTab>("structure");
  const diffByKey = useMemo(
    () => new Map((fieldDiffs ?? []).map((diff) => [diff.key, diff])),
    [fieldDiffs],
  );
  const removedItems = useMemo(
    () =>
      previousPlan
        ? computeRemovedPlanItems(previousPlan, plan)
        : NO_REMOVED_ITEMS,
    [previousPlan, plan],
  );

  // A reveal request starts by switching to the target field's tab; the
  // collapsibles and field anchors listening on PlanRevealContext take it
  // from there once the tab's content renders.
  useEffect(() => {
    if (revealTarget) setActiveTab(tabForFieldKey(revealTarget.key));
  }, [revealTarget]);
  const [showAddAttribute, setShowAddAttribute] = useState(false);
  const [showAddTestingTable, setShowAddTestingTable] = useState(false);
  const [editingTable, setEditingTable] =
    useState<WorkStepDraftTestingTable | null>(null);

  // Pending destructive action; while set, the confirmation dialog is shown and
  // its `onConfirm` runs the actual removal.
  const [pendingDelete, setPendingDelete] = useState<DeleteRequest | null>(
    null,
  );

  const requestDelete = useCallback<RequestDelete>((opts) => {
    setPendingDelete(opts);
  }, []);

  // Local drafts for the Name/Description text fields; committed on blur so we
  // don't emit a plan patch on every keystroke. Re-synced when the plan changes,
  // except while focused (see useCommittableDraft) so an unrelated patch can't
  // clobber an in-progress edit.
  const {
    draft: nameDraft,
    setDraft: setNameDraft,
    onFocus: onNameFocus,
    endEditing: endNameEditing,
  } = useCommittableDraft(plan.workStepName);
  const {
    draft: descDraft,
    setDraft: setDescDraft,
    onFocus: onDescFocus,
    endEditing: endDescEditing,
  } = useCommittableDraft(plan.workStepDescription);

  const isEditable = editable && !disabled;
  const nameLocked = optroLockedFields.includes("work_step_name");
  const descriptionLocked = optroLockedFields.includes("work_step_description");

  const handleWorkStepNameChange = useCallback(
    (value: string) => {
      onPlanChange?.({ workStepName: value });
    },
    [onPlanChange],
  );

  const handleWorkStepDescriptionChange = useCallback(
    (value: string) => {
      onPlanChange?.({ workStepDescription: value });
    },
    [onPlanChange],
  );

  const handleRemoveAttribute = useCallback(
    (reference: string) => {
      onPlanChange?.({ removeAttributeRefs: [reference] });
    },
    [onPlanChange],
  );

  const handleAddAttribute = useCallback(
    (attribute: WorkStepProposedAttribute) => {
      onPlanChange?.({ addAttributes: [attribute] });
    },
    [onPlanChange],
  );

  const handleUpdateAttributeStatement = useCallback(
    (attribute: WorkStepProposedAttribute, statement: string) => {
      // The spread carries the attribute's id, matched by id on the way back so the row
      // (and the attribute-test columns pointing at it) is preserved.
      onPlanChange?.({
        updateAttributes: [{ ...attribute, statement }],
      });
    },
    [onPlanChange],
  );

  const handleUpdateAttributeReference = useCallback(
    (attribute: WorkStepProposedAttribute, reference: string) => {
      // Re-lettering the code: the id rides along in the spread, so apply_patch matches by
      // id, keeps the row, and rewrites the reference across the testing tables/columns.
      onPlanChange?.({
        updateAttributes: [{ ...attribute, reference }],
      });
    },
    [onPlanChange],
  );

  const handleRemoveTestingTable = useCallback(
    (tableName: string) => {
      onPlanChange?.({ removeTestingTableNames: [tableName] });
    },
    [onPlanChange],
  );

  const handleAddTestingTable = useCallback(
    (table: WorkStepDraftTestingTable) => {
      onPlanChange?.({ addTestingTables: [table] });
    },
    [onPlanChange],
  );

  const handleUpdateTestingTable = useCallback(
    (table: WorkStepDraftTestingTable) => {
      onPlanChange?.({ updateTestingTables: [table] });
    },
    [onPlanChange],
  );

  const proposedAttributes = plan.proposedAttributes ?? [];
  const attributeReferences = proposedAttributes.map((attr) => attr.reference);

  const namePreviousValue = previousValueFor(
    diffByKey.get(fieldKeys.workStepName()),
    plan.workStepName,
  );
  const descriptionPreviousValue = previousValueFor(
    diffByKey.get(fieldKeys.workStepDescription()),
    plan.workStepDescription,
  );

  return (
    <PlanFieldDiffContext.Provider value={diffByKey}>
      <PlanRevealContext.Provider value={revealTarget}>
        <PlanHighlightContext.Provider value={highlightedFieldKey}>
          <Card className="h-full overflow-hidden flex flex-col">
            <CardHeader className="px-6 py-4 flex-shrink-0 space-y-0 border-b border-stone-200">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  {headerLabel && (
                    <div className="flex items-center gap-2 text-xs font-medium text-stone-500">
                      {headerLabel}
                      {saving && (
                        <span className="inline-flex items-center gap-1 font-normal text-stone-400">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Saving…
                        </span>
                      )}
                    </div>
                  )}
                  <CardTitle
                    className="text-base font-semibold truncate"
                    title={plan.workStepName}
                  >
                    {plan.workStepName}
                  </CardTitle>
                </div>
                <div className="flex-shrink-0 inline-flex self-start rounded-lg bg-stone-100 p-1 text-xs">
                  {PLAN_TABS.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => setActiveTab(tab.id)}
                        className={cn(
                          "flex items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 py-1 font-medium transition-colors",
                          activeTab === tab.id
                            ? "bg-white text-stone-900 shadow-sm"
                            : "text-stone-500 hover:text-stone-800",
                        )}
                      >
                        <Icon className="h-3.5 w-3.5" />
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </CardHeader>

            <CardContent className="flex-1 min-h-0 overflow-y-auto pt-4 space-y-0">
              <UntestedAttributesNotice
                className="mb-4"
                untestedReferences={plan.untestedAttributeReferences}
                attributes={plan.proposedAttributes}
                analyticsOwnerKind="workStep"
              />
              {activeTab === "instructions" ? (
                <CollabTestInstructionsView
                  plan={plan}
                  editable={isEditable}
                  // The view describes the edit; merging it onto this entity's
                  // own table is what keeps the table's concrete type intact.
                  onTableUpdate={(table, changes) =>
                    onPlanChange?.({
                      updateTestingTables: [{ ...table, ...changes }],
                    })
                  }
                  requestDelete={requestDelete}
                  removedItems={removedItems}
                  analyticsOwnerKind="workStep"
                />
              ) : (
                <div className="space-y-4">
                  {/* Name / Description as labeled fields */}
                  <Field label="Name">
                    <FieldRevealAnchor fieldKey={fieldKeys.workStepName()}>
                      <WithPreviousValue previousValue={namePreviousValue}>
                        <OptroManagedField
                          entityLabel="the work step"
                          locked={nameLocked}
                        >
                          <Input
                            value={nameDraft}
                            onChange={(e) => setNameDraft(e.target.value)}
                            onFocus={onNameFocus}
                            onBlur={() => {
                              endNameEditing();
                              const next = nameDraft.trim();
                              if (next && next !== plan.workStepName) {
                                handleWorkStepNameChange(next);
                              }
                            }}
                            disabled={!isEditable || nameLocked}
                            placeholder="Work step name"
                            className={cn(
                              namePreviousValue !== undefined &&
                                "border-0 shadow-none focus-visible:ring-0",
                            )}
                          />
                        </OptroManagedField>
                      </WithPreviousValue>
                    </FieldRevealAnchor>
                  </Field>
                  <Field label="Description">
                    <FieldRevealAnchor
                      fieldKey={fieldKeys.workStepDescription()}
                    >
                      <WithPreviousValue
                        previousValue={descriptionPreviousValue}
                      >
                        <OptroManagedField
                          entityLabel="the work step"
                          locked={descriptionLocked}
                        >
                          <AutoResizingTextarea
                            value={descDraft}
                            onChange={(e) => setDescDraft(e.target.value)}
                            onFocus={onDescFocus}
                            onBlur={() => {
                              endDescEditing();
                              if (descDraft !== plan.workStepDescription) {
                                handleWorkStepDescriptionChange(descDraft);
                              }
                            }}
                            disabled={!isEditable || descriptionLocked}
                            placeholder="Describe the work step..."
                            minHeight={64}
                            className={cn(
                              descriptionPreviousValue !== undefined &&
                                "border-0 shadow-none focus-visible:ring-0",
                            )}
                          />
                        </OptroManagedField>
                      </WithPreviousValue>
                    </FieldRevealAnchor>
                  </Field>

                  {/* Attributes */}
                  <CollapsibleSection
                    title={`Attributes (${proposedAttributes.length})`}
                    onAdd={() => setShowAddAttribute(true)}
                    showAdd={isEditable}
                    disabled={disabled}
                    revealPrefixes={["attr:"]}
                  >
                    <div className="space-y-1.5">
                      {withRemovedInPlace(
                        proposedAttributes,
                        removedItems.attributes,
                      ).map((entry) => {
                        const attr = entry.item;
                        if (entry.kind === "removed") {
                          return (
                            <FieldRevealAnchor
                              key={`removed-${attr.reference}`}
                              fieldKey={fieldKeys.attribute(attr.reference)}
                            >
                              <div className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 px-3 py-2">
                                <span className="min-w-0 flex-1">
                                  <span className="flex flex-wrap items-center gap-2">
                                    {attr.reference && (
                                      <span className="text-sm font-semibold text-red-400 line-through">
                                        {attr.reference}
                                      </span>
                                    )}
                                    <RemovedBadge />
                                  </span>
                                  <span className="block text-sm text-red-400/80 line-through">
                                    {attr.statement}
                                  </span>
                                </span>
                              </div>
                            </FieldRevealAnchor>
                          );
                        }
                        const statementPreviousValue = previousValueFor(
                          diffByKey.get(fieldKeys.attribute(attr.reference)),
                          attr.statement,
                        );
                        return (
                          <FieldRevealAnchor
                            key={entry.index}
                            fieldKey={fieldKeys.attribute(attr.reference)}
                          >
                            <div
                              className={cn(
                                EDIT_CARD_CLASS,
                                "flex items-start gap-3 px-3 py-2",
                              )}
                            >
                              <span className="min-w-0 flex-1 space-y-1">
                                {isEditable ? (
                                  <EditableAttributeReference
                                    value={attr.reference}
                                    onCommit={(v) =>
                                      handleUpdateAttributeReference(attr, v)
                                    }
                                  />
                                ) : (
                                  attr.reference && (
                                    <span className="text-sm font-semibold text-stone-800">
                                      {attr.reference}
                                    </span>
                                  )
                                )}
                                {isEditable ? (
                                  <EditableDetailField
                                    label=""
                                    value={attr.statement}
                                    editable
                                    placeholder="What this attribute represents..."
                                    onCommit={(v) =>
                                      handleUpdateAttributeStatement(attr, v)
                                    }
                                    fieldKey={fieldKeys.attribute(
                                      attr.reference,
                                    )}
                                    // The card is already a FieldRevealAnchor for
                                    // this key; don't flash the inner field too.
                                    suppressReveal
                                  />
                                ) : (
                                  <>
                                    {statementPreviousValue !== undefined && (
                                      <span className="block text-sm text-red-400 line-through">
                                        {statementPreviousValue}
                                      </span>
                                    )}
                                    <span className="block text-sm text-stone-500">
                                      {attr.statement}
                                    </span>
                                  </>
                                )}
                              </span>
                              {isEditable && (
                                <button
                                  type="button"
                                  onClick={() =>
                                    requestDelete({
                                      title: "Remove attribute?",
                                      description: `${attr.reference ? `${attr.reference} — ` : ""}"${attr.statement}" will be removed from the plan.`,
                                      onConfirm: () =>
                                        handleRemoveAttribute(attr.reference),
                                    })
                                  }
                                  className="shrink-0 rounded-sm p-1 text-stone-400 opacity-0 transition-opacity hover:bg-stone-200 hover:text-stone-600 group-hover:opacity-100"
                                >
                                  <X className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </div>
                          </FieldRevealAnchor>
                        );
                      })}
                      {proposedAttributes.length === 0 && (
                        <p className="text-sm text-stone-500">
                          No attributes yet.
                        </p>
                      )}
                    </div>
                  </CollapsibleSection>

                  {/* Testing Tables */}
                  <CollapsibleSection
                    title={`Testing Tables (${plan.testingTables.length})`}
                    onAdd={() => setShowAddTestingTable(true)}
                    showAdd={isEditable}
                    disabled={disabled}
                    revealPrefixes={["table:"]}
                  >
                    <div className="space-y-2">
                      {withRemovedInPlace(
                        plan.testingTables,
                        removedItems.tables,
                      ).map((entry) => {
                        const table = entry.item;
                        if (entry.kind === "removed") {
                          return (
                            <FieldRevealAnchor
                              key={`removed-${table.name}`}
                              fieldKey={fieldKeys.tableName(table.name)}
                            >
                              <div className="space-y-2.5 rounded-md border border-red-200 bg-red-50 p-3">
                                <div className="flex min-w-0 flex-wrap items-center gap-2">
                                  <span className="text-sm font-semibold text-red-400 line-through">
                                    {table.name}
                                  </span>
                                  <RemovedBadge />
                                </div>
                                {table.purpose && (
                                  <p className="text-xs leading-relaxed text-red-400/80 line-through">
                                    {table.purpose}
                                  </p>
                                )}
                              </div>
                            </FieldRevealAnchor>
                          );
                        }
                        const tableIndex = entry.index;
                        const purposePreviousValue = previousValueFor(
                          diffByKey.get(fieldKeys.tablePurpose(table.name)),
                          table.purpose,
                        );
                        return (
                          <FieldRevealAnchor
                            key={tableIndex}
                            fieldKey={fieldKeys.tableName(table.name)}
                          >
                            <div
                              className={cn(EDIT_CARD_CLASS, "space-y-2.5 p-3")}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex min-w-0 flex-wrap items-center gap-2">
                                  <span className="text-sm font-semibold text-stone-800">
                                    {table.name}
                                  </span>
                                  <Badge
                                    variant="secondary"
                                    className="rounded-sm px-1.5 py-0.5 font-normal capitalize"
                                  >
                                    {table.procedureType}
                                  </Badge>
                                  <FieldRevealAnchor
                                    fieldKey={fieldKeys.tableAttributeRefs(
                                      table.name,
                                    )}
                                    className="flex flex-wrap items-center gap-2 px-1"
                                  >
                                    {table.attributeReferences.map((ref) => (
                                      <span
                                        key={ref}
                                        className="rounded-sm bg-stone-200 px-1.5 py-0.5 text-[11px] text-stone-600"
                                      >
                                        {ref}
                                      </span>
                                    ))}
                                  </FieldRevealAnchor>
                                </div>
                                {isEditable && (
                                  <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                                    <button
                                      type="button"
                                      onClick={() => setEditingTable(table)}
                                      className="rounded-sm p-1 text-stone-400 hover:bg-stone-200 hover:text-stone-600"
                                    >
                                      <Pencil className="h-3.5 w-3.5" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        requestDelete({
                                          title: "Remove testing table?",
                                          description: `"${table.name}" and all its columns will be removed from the plan.`,
                                          onConfirm: () =>
                                            handleRemoveTestingTable(
                                              table.name,
                                            ),
                                        })
                                      }
                                      className="rounded-sm p-1 text-stone-400 hover:bg-stone-200 hover:text-stone-600"
                                    >
                                      <X className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                )}
                              </div>
                              {(purposePreviousValue !== undefined ||
                                !!table.purpose) && (
                                <FieldRevealAnchor
                                  fieldKey={fieldKeys.tablePurpose(table.name)}
                                >
                                  {purposePreviousValue !== undefined && (
                                    <p className="text-xs leading-relaxed text-red-400 line-through">
                                      {purposePreviousValue}
                                    </p>
                                  )}
                                  {table.purpose && (
                                    <p className="text-xs leading-relaxed text-stone-500">
                                      {table.purpose}
                                    </p>
                                  )}
                                </FieldRevealAnchor>
                              )}
                              <ColumnsTable table={table} />
                            </div>
                          </FieldRevealAnchor>
                        );
                      })}
                      {plan.testingTables.length === 0 && (
                        <p className="text-sm text-stone-500">
                          No testing tables yet.
                        </p>
                      )}
                    </div>
                  </CollapsibleSection>

                  {/* AI Reasoning */}
                  <CollapsibleSection title="AI Reasoning" defaultOpen={false}>
                    <div className="p-3 bg-stone-50 rounded-md text-sm text-stone-600 whitespace-pre-wrap">
                      {plan.reasoning || "No reasoning provided"}
                    </div>
                  </CollapsibleSection>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Dialogs */}
          <AddAttributeDialog
            open={showAddAttribute}
            onClose={() => setShowAddAttribute(false)}
            onAdd={handleAddAttribute}
          />
          <TestingTableDialog
            open={showAddTestingTable}
            onClose={() => setShowAddTestingTable(false)}
            onSave={handleAddTestingTable}
            attributeReferences={attributeReferences}
            mode="add"
            seededBases={plan}
          />
          <TestingTableDialog
            open={editingTable !== null}
            onClose={() => setEditingTable(null)}
            onSave={handleUpdateTestingTable}
            attributeReferences={attributeReferences}
            initialTable={editingTable ?? undefined}
            mode="edit"
            seededBases={plan}
          />
          <ConfirmDeleteDialog
            open={pendingDelete !== null}
            onOpenChange={(o) => !o && setPendingDelete(null)}
            title={pendingDelete?.title}
            description={pendingDelete?.description ?? ""}
            blocked={pendingDelete?.blocked === true}
            onConfirm={() => {
              // Clear before running so an onConfirm that re-requests a blocked
              // explanation (re-checked against the latest plan) is what remains.
              const pending = pendingDelete;
              setPendingDelete(null);
              if (pending && !pending.blocked) {
                pending.onConfirm();
              }
            }}
          />
        </PlanHighlightContext.Provider>
      </PlanRevealContext.Provider>
    </PlanFieldDiffContext.Provider>
  );
};
